'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/classnames'

import ReviewLightbox from './review-lightbox'
import { LANDING_REVIEWS_COUNT, Review, ReviewSource } from './reviews-data'

const sourceLabel: Record<ReviewSource, string> = {
  whatsapp: 'WhatsApp',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  reddit: 'Reddit',
  klix: 'Klix',
}

const sourceClass: Record<ReviewSource, string> = {
  whatsapp: 'bg-[#25D366]/80 text-white',
  facebook: 'bg-[#1877F2]/80 text-white',
  linkedin: 'bg-[#0A66C2]/80 text-white',
  instagram: 'bg-gradient-to-r from-[#f9ce34]/80 via-[#ee2a7b]/80 to-[#6228d7]/80 text-white',
  reddit: 'bg-[#FF4500]/80 text-white',
  klix: 'bg-[#41aff0]/80 text-white',
}

/**
 * Top of the review screenshot with its source tag and a short quote; the whole card opens the lightbox.
 * `zoomOverlay` swaps the magnifier button for one over the image on phones.
 */
export const ReviewCard = ({ review, onOpen, zoomOverlay }: { review: Review; onOpen: () => void; zoomOverlay?: boolean }) => {
  const { t } = useTranslation()
  const [missing, setMissing] = useState(false)

  return (
    <button
      type='button'
      onClick={onOpen}
      aria-label={`${t('Landing.Reviews.Enlarge')}: ${review.alt}`}
      className='group flex w-full cursor-zoom-in flex-col overflow-hidden rounded-2xl bg-white sm:rounded-3xl text-left shadow-[0_4px_20px_rgba(36,45,82,0.08)] ring-1 ring-slate-200 transition-shadow hover:shadow-[0_8px_28px_rgba(36,45,82,0.14)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F3603C]'
    >
      <span className='relative block [aspect-ratio:7/6] w-full overflow-hidden bg-slate-100'>
        {!missing && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={review.path}
            alt={review.alt}
            width={850}
            height={1020}
            loading='lazy'
            draggable={false}
            className='h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.02]'
            onError={() => setMissing(true)}
          />
        )}
        <span
          className={cn(
            'absolute right-2 top-2 rounded-full px-2.5 py-1.5 text-xs font-bold sm:right-3 sm:top-3 sm:px-3.5 sm:py-2 sm:text-sm leading-none shadow-md backdrop-blur-sm',
            sourceClass[review.type],
          )}
        >
          {sourceLabel[review.type]}
        </span>
        {zoomOverlay && (
          <span className='absolute inset-x-0 bottom-0 flex h-1/2 items-end justify-end bg-gradient-to-t from-slate-900/40 to-transparent p-2 sm:hidden' aria-hidden>
            <span className='flex size-9 items-center justify-center rounded-full bg-white/90 text-blue-950 shadow-md'>
              <MagnifyingGlassPlusIcon className='size-5 stroke-2' />
            </span>
          </span>
        )}
      </span>
      <span className='flex flex-1 items-center justify-between gap-3 border-t border-slate-100 p-3 sm:gap-4 sm:p-5'>
        <span className='text-[15px] font-bold leading-snug text-blue-950 sm:text-lg'>„{review.quote}“</span>
        <span
          className={cn('flex size-10 shrink-0 items-center justify-center rounded-full bg-[#F3603C] text-white transition-colors group-hover:brightness-95 sm:size-11', zoomOverlay && 'max-sm:hidden')}
          aria-hidden
        >
          <MagnifyingGlassPlusIcon className='size-5 stroke-2 sm:size-6' />
        </span>
      </span>
    </button>
  )
}

/** All reviews in a grid for the /reviews page; a click opens the lightbox over the whole list. */
export const ReviewGrid = ({ reviews }: { reviews: Review[] }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <>
      <div className='mt-8 grid grid-cols-2 gap-x-4 gap-y-6 sm:gap-x-6 sm:gap-y-8 lg:grid-cols-3 lg:gap-x-8 lg:gap-y-10'>
        {reviews.map((review, index) => (
          <ReviewCard review={review} key={review.path} onOpen={() => setOpenIndex(index)} zoomOverlay />
        ))}
      </div>
      <ReviewLightbox reviews={reviews} index={openIndex} onChange={setOpenIndex} onClose={() => setOpenIndex(null)} />
    </>
  )
}

const scrollStep = (el: HTMLDivElement) => {
  const card = el.firstElementChild as HTMLElement | null
  const gap = parseFloat(getComputedStyle(el).columnGap) || 0
  return (card?.offsetWidth ?? 300) + gap
}

interface ReviewsProps {
  /** All reviews, already shuffled on the server so the order differs per visit without a hydration mismatch. */
  reviews: Review[]
}

const Reviews = ({ reviews }: ReviewsProps) => {
  const { t, i18n } = useTranslation()
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [page, setPage] = useState(0)
  const [pageCount, setPageCount] = useState(1)
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const visible = reviews.slice(0, LANDING_REVIEWS_COUNT)

  const sync = () => {
    const el = scrollerRef.current
    if (!el) return
    const step = scrollStep(el)
    const gap = step - ((el.firstElementChild as HTMLElement | null)?.offsetWidth ?? 300)
    const fits = Math.max(1, Math.floor((el.clientWidth + gap) / step))
    const count = Math.max(1, visible.length - fits + 1)
    const index = Math.min(count - 1, Math.max(0, Math.round(el.scrollLeft / step)))
    setPageCount(count)
    setPage(current => (current === index ? current : index))
  }

  useEffect(() => {
    sync()
    window.addEventListener('resize', sync)
    return () => window.removeEventListener('resize', sync)
  }, [])

  const goTo = (index: number) => {
    const el = scrollerRef.current
    if (!el) return
    const next = Math.min(pageCount - 1, Math.max(0, index))
    el.scrollTo({ left: next * scrollStep(el), behavior: 'smooth' })
  }

  const atStart = page <= 0
  const atEnd = page >= pageCount - 1
  const arrowClassName = (disabled: boolean) =>
    cn(
      'flex size-11 items-center justify-center rounded-full border shadow-sm transition-colors',
      disabled ? 'border-slate-100 bg-slate-50 text-slate-300' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
    )

  return (
    <section className='bg-white py-8' aria-labelledby='reviews-heading' data-track-section='reviews'>
      <div className='mx-auto max-w-5xl px-4'>
        <div className='text-left md:text-center'>
          <p className='text-[13px] font-semibold uppercase tracking-[0.16em] text-[#F3603C]'>{t('Landing.Reviews.Eyebrow')}</p>
          <h2 id='reviews-heading' className='mt-3 scroll-mt-28 text-3xl font-bold tracking-tight text-blue-950'>
            {t('Landing.Reviews.Heading')}
          </h2>
          <p className='mt-4 max-w-xl text-lg leading-relaxed text-slate-500 md:mx-auto'>{t('Landing.Reviews.Description')}</p>
        </div>

        {/* Padding with matching negative margins keeps the card rings and shadows from being clipped by the scroll container. */}
        <div
          ref={scrollerRef}
          onScroll={sync}
          className='-mx-2 -my-2 mt-4 flex snap-x snap-mandatory scroll-px-2 gap-4 overflow-x-auto scroll-smooth px-2 py-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
          aria-label={t('Landing.Reviews.ListLabel')}
        >
          {visible.map((review, index) => (
            <div key={review.path} className='flex w-[280px] shrink-0 snap-start sm:w-[300px]'>
              <ReviewCard review={review} onOpen={() => setOpenIndex(index)} />
            </div>
          ))}
        </div>
        {/* Only the carousel reviews page in the lightbox; the rest live on /reviews. */}
        <ReviewLightbox reviews={visible} index={openIndex} onChange={setOpenIndex} onClose={() => setOpenIndex(null)} />

        <div className='mt-6 flex items-center justify-center gap-6'>
          <button type='button' aria-label={t('Landing.Reviews.Previous')} disabled={atStart} onClick={() => goTo(page - 1)} className={arrowClassName(atStart)}>
            <ChevronLeftIcon className='size-5' />
          </button>
          <div className='flex items-center gap-1' role='tablist' aria-label={t('Landing.Reviews.Pages')}>
            {Array.from({ length: pageCount }, (_, index) => (
              <button
                key={index}
                type='button'
                role='tab'
                aria-selected={index === page}
                aria-label={t('Landing.Reviews.Page', { page: index + 1, count: pageCount })}
                className='p-1.5'
                onClick={() => goTo(index)}
              >
                <span className={cn('block h-2.5 rounded-full transition-all', index === page ? 'w-6 bg-[#F3603C]' : 'w-2.5 bg-slate-300')} />
              </button>
            ))}
          </div>
          <button type='button' aria-label={t('Landing.Reviews.Next')} disabled={atEnd} onClick={() => goTo(page + 1)} className={arrowClassName(atEnd)}>
            <ChevronRightIcon className='size-5' />
          </button>
        </div>

        {reviews.length > visible.length && (
          <div className='mt-6 flex justify-center'>
            <Link
              href={`/${i18n.language}/reviews`}
              data-track-cta='reviews_view_all'
              className='inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 text-base font-semibold text-blue-950 shadow-sm transition-colors hover:bg-slate-50'
            >
              {t('Landing.Reviews.ViewAll', { count: reviews.length })}
              <ArrowRightIcon className='size-5' aria-hidden />
            </Link>
          </div>
        )}
      </div>
    </section>
  )
}

export default Reviews
